/**
 * AMB-220 — single source of truth for event-type display labels.
 *
 * Old naming had two parallel vocabularies — BE "Лайв-стрим" / "Live
 * stream" vs FE "Лайв-сессия", and "Практика из библиотеки" vs
 * "Лайв-практика" — which confused users and made the cards / filter
 * chips / section headers inconsistent across the calendar, profile,
 * dashboard, and event details surfaces.
 *
 * New unified vocabulary:
 *
 *   Quick Room    / Быстрая комната  (unchanged)
 *   Live Practice / Live-практика    (was: Library Practice / "Практика из библиотеки" / "Лайв-практика")
 *   Live Session  / Live-сессия      (was: Live Stream / "Лайв-стрим" / "Лайв-сессия")
 *   Offline Event / Офлайн-встреча   (unchanged; legacy "Офлайн" pill kept where space-constrained)
 *
 * Keep this file the only place that hardcodes the user-facing strings.
 * Hardcoded literals elsewhere drift on each rename — route through
 * the helpers below.
 */

export type EventTypeKey =
  | 'quick_room'
  | 'live_practice'
  | 'live_session'
  | 'offline_event';

// CORE-1865 — `live_practice` is a technical key of the old model; for the viewer a room
// is a «Live-сессия» like a stream, so both keys read the same.
export type EventLanguage = 'ru' | 'en';

/** Long body label (sentence-case). Used in titles, descriptions, hero copy. */
export function eventTypeLabel(key: EventTypeKey, language: EventLanguage): string {
  if (language === 'ru') {
    switch (key) {
      case 'quick_room':    return 'Быстрая комната';
      case 'live_practice': return 'Live-сессия';
      case 'live_session':  return 'Live-сессия';
      case 'offline_event': return 'Офлайн-встреча';
    }
  }
  switch (key) {
    case 'quick_room':    return 'Quick Room';
    case 'live_practice': return 'Live Session';
    case 'live_session':  return 'Live Session';
    case 'offline_event': return 'Offline Event';
  }
}

/** Plural form used in section headers and filter chip labels. */
export function eventTypeLabelPlural(key: EventTypeKey, language: EventLanguage): string {
  if (language === 'ru') {
    switch (key) {
      case 'quick_room':    return 'Быстрые комнаты';
      case 'live_practice': return 'Live-сессии';
      case 'live_session':  return 'Live-сессии';
      case 'offline_event': return 'Офлайн-встречи';
    }
  }
  switch (key) {
    case 'quick_room':    return 'Quick Rooms';
    case 'live_practice': return 'Live Sessions';
    case 'live_session':  return 'Live Sessions';
    case 'offline_event': return 'Offline Events';
  }
}

/**
 * Genitive plural ("Найдено N Live-сессий, M Live-практик"). RU only —
 * EN doesn't decline. Keep aligned to the plural form above.
 */
export function eventTypeLabelGenitivePlural(key: EventTypeKey, language: EventLanguage): string {
  if (language === 'ru') {
    switch (key) {
      case 'quick_room':    return 'Быстрых комнат';
      case 'live_practice': return 'Live-сессий';
      case 'live_session':  return 'Live-сессий';
      case 'offline_event': return 'Офлайн-встреч';
    }
  }
  // EN falls back to the regular plural — "5 Live Sessions found".
  return eventTypeLabelPlural(key, 'en');
}

/**
 * Short upper-case pill label, used on cards/badges where space is tight.
 * Russian only — EN UI also caps the same words.
 */
export function eventTypePill(key: EventTypeKey): string {
  switch (key) {
    case 'quick_room':    return 'БЫСТРАЯ КОМНАТА';
    case 'live_practice': return 'LIVE-ПРАКТИКА';
    case 'live_session':  return 'LIVE-СЕССИЯ';
    case 'offline_event': return 'ОФЛАЙН';
  }
}

/**
 * Map a BE LiveKindDto.code ("LiveStream" / "LivePractice" / "Offline")
 * into our EventTypeKey. The BE entity names stay; only the FE display
 * vocabulary changes.
 */
export function resolveEventTypeFromBeKind(beCode: string | undefined | null): EventTypeKey | null {
  switch (beCode) {
    case 'LiveStream':   return 'live_session';
    case 'LivePractice': return 'live_practice';
    case 'Offline':      return 'offline_event';
    default:             return null;
  }
}
